/**
 * Dashboard Widget Handlers
 * Handles undo/redo for dashboard widget operations (add, remove, resize, move, config)
 */

import type { Operation, DashboardOperation } from '@/shared/types/projectData';
import type { OperationHandler } from '../types';

/**
 * Register all dashboard widget operation handlers
 */
export function registerDashboardHandlers(
  handlers: Map<string, OperationHandler>,
  applyDashboardOp?: (op: DashboardOperation) => Promise<void>
): void {
  if (!applyDashboardOp) return;

  // Add widget
  handlers.set('add-widget', {
    undo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId) {
        await applyDashboardOp({
          type: 'remove-widget',
          widgetId: op.widgetId,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.widgetData) {
        await applyDashboardOp({
          type: 'add-widget',
          widgetId: op.widgetId,
          widgetType: op.widgetType,
          widgetData: op.widgetData,
          newPosition: op.newPosition,
          newSize: op.newSize,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    }
  });

  // Remove widget
  handlers.set('remove-widget', {
    undo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.previousData) {
        await applyDashboardOp({
          type: 'add-widget',
          widgetId: op.widgetId,
          widgetType: op.widgetType,
          widgetData: op.previousData,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId) {
        await applyDashboardOp({
          type: 'remove-widget',
          widgetId: op.widgetId,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    }
  });

  // Resize widget
  handlers.set('resize-widget', {
    undo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.previousSize) {
        await applyDashboardOp({
          type: 'resize-widget',
          widgetId: op.widgetId,
          newSize: op.previousSize,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.newSize) {
        await applyDashboardOp({
          type: 'resize-widget',
          widgetId: op.widgetId,
          newSize: op.newSize,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    }
  });

  // Move widget
  handlers.set('move-widget', {
    undo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.previousPosition) {
        await applyDashboardOp({
          type: 'move-widget',
          widgetId: op.widgetId,
          newPosition: op.previousPosition,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.newPosition) {
        await applyDashboardOp({
          type: 'move-widget',
          widgetId: op.widgetId,
          newPosition: op.newPosition,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    }
  });

  // Update widget config
  handlers.set('update-widget-config', {
    undo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.previousConfig) {
        await applyDashboardOp({
          type: 'update-widget-config',
          widgetId: op.widgetId,
          newConfig: op.previousConfig,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as DashboardOperation;
      if (op.widgetId && op.newConfig) {
        await applyDashboardOp({
          type: 'update-widget-config',
          widgetId: op.widgetId,
          newConfig: op.newConfig,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    }
  });
}
